import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin, BadgeCheck, ShieldAlert, ClipboardCheck } from "lucide-react";
import { Reveal, MaskedLine } from "./motion/Reveal";

const CHAPTERS = [
  { key: "01", Icon: MapPin },
  { key: "02", Icon: BadgeCheck },
  { key: "03", Icon: ShieldAlert },
  { key: "04", Icon: ClipboardCheck },
];

export default function Manifesto() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Big background numeral drifts slowly upward as user scrolls through the section
  const numeralY = useTransform(scrollYProgress, [0, 1], ["10%", "-25%"]);
  const numeralOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 0.05, 0.05, 0]);

  return (
    <section
      ref={ref}
      id="belofte"
      data-testid="manifesto-section"
      className="section relative bg-white border-t border-slate-100 overflow-hidden"
    >
      {/* Giant background numeral — editorial */}
      <motion.div
        aria-hidden
        style={{ y: numeralY, opacity: numeralOpacity }}
        className="pointer-events-none absolute -right-8 md:-right-16 top-0 font-display text-[28rem] md:text-[42rem] leading-none text-[#0A1128] select-none"
      >
        04
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        {/* Header */}
        <div className="mb-14 md:mb-20 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="accent-bar" />
                <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                  {t("manifesto.eyebrow")}
                </span>
              </div>
            </Reveal>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-[#0A1128] leading-[0.95]">
              <span className="block overflow-hidden">
                <MaskedLine text={t("manifesto.title_1")} wordDelay={0.06} />
              </span>
              <span className="block overflow-hidden text-[#FF5A1F]">
                <MaskedLine text={t("manifesto.title_2")} wordDelay={0.06} delay={0.15} />
              </span>
            </h2>
          </div>
          <Reveal className="lg:col-span-4" delay={0.1}>
            <p className="font-display text-[#0A1128]/70 text-sm md:text-base uppercase tracking-[0.2em] leading-relaxed border-l-2 border-[#FF5A1F] pl-5">
              {t("manifesto.kicker")}
            </p>
          </Reveal>
        </div>

        {/* Chapters — editorial numbered list */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-14 md:gap-y-20">
          {CHAPTERS.map(({ key, Icon }, i) => (
            <motion.article
              key={key}
              data-testid={`manifesto-chapter-${key}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px 0px" }}
              transition={{ duration: 0.9, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative"
            >
              {/* Chapter number — enormous */}
              <div className="flex items-baseline gap-6 mb-5">
                <span className="font-display text-[6rem] md:text-[9rem] leading-[0.85] text-[#0A1128] group-hover:text-[#FF5A1F] transition-colors duration-500 tabular-nums">
                  {key}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5A1F] font-bold mb-1">
                    {t(`manifesto.chapters.${key}.label`)}
                  </div>
                  <div className="w-full h-px bg-slate-200 relative overflow-hidden">
                    <motion.span
                      className="absolute inset-y-0 left-0 bg-[#FF5A1F]"
                      initial={{ width: "0%" }}
                      whileInView={{ width: "100%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                </div>
                <span className="w-11 h-11 md:w-14 md:h-14 border-2 border-[#0A1128] flex items-center justify-center group-hover:bg-[#0A1128] transition-colors">
                  <Icon className="w-5 h-5 md:w-6 md:h-6 text-[#0A1128] group-hover:text-[#FF5A1F] transition-colors" strokeWidth={2.2} />
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#0A1128] mb-3 leading-tight max-w-lg">
                {t(`manifesto.chapters.${key}.title`)}
              </h3>
              <p className="text-slate-600 leading-relaxed text-base max-w-lg">
                {t(`manifesto.chapters.${key}.desc`)}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
